import React, { useState, useEffect } from 'react';
import { loadTeamTransactionTasks, completeTransactionTask } from '../services/transactionTasks';
import { useNavigate } from 'react-router-dom';
import { goldButton } from '../styles/gold';
import { useAuth } from '../context/AuthContext';
import { database } from '../firebaseConfig';
import { ref, get } from 'firebase/database';
import { pendingCommissionBreakdown, takeHome } from '../services/commissionMath';
import { isTeamLeaderEmail } from '../config/commissionPlans';
import { getTeamTransactions } from '../services/transactionService';
import TransactionTasksList from './TransactionTasksList';
import { getTasks, updateTask } from '../services/taskService';
import { isAutomatic, isMyTask, isRoleMine, hiddenFromLimited } from '../services/taskRules';
import { showsAnnouncements, showsLeadTasks } from '../config/roleAccess';
import AnnouncementFeed from './AnnouncementFeed';
import PersonPeekPanel, { PersonName } from './personPeek/PersonPeekPanel';
import LeadTasksPanel from './LeadTasksPanel';
import { OutreachCard } from './outreach/MyOutreach';
import EmailApprovalsPanel from './EmailApprovalsPanel';
import NicheNewsSection from './NicheNewsSection';
import { todayET } from '../utils/dueDates';

// Turn a saved transaction record into the shape the dashboard cards use.
const toCard = (t) => ({
  id: t.id,
  propertyAddress: t.propertyAddress || t.address || 'Address TBD',
  type: String(t.type || '').toLowerCase().includes('buyer') ? 'buyer' : 'seller-listing',
  status: t.status || 'pending',
  price: t.price || t.purchasePrice || null,
  clientName: t.clientName || '',
  keyDate: t.keyDate || '',
  isSample: !!t.isSample,
});

// Fallback headshot for admin accounts that have not uploaded one yet.
const ADMIN_FALLBACK_PHOTO = '/kelly-headshot-600.jpg';
// import mockTasks - removed


const SAMPLE_TRANSACTIONS = [
  { id: 'sample-1', type: 'seller-listing', status: 'listing-active', propertyAddress: '123 Sample Way, Estero, FL 33928', price: 725000, firstName: 'Maria', lastName: 'Alvarez', clientName: 'Maria Alvarez', keyDate: 'Listed Oct 1', closingDate: '2026-12-15', expirationDate: '2027-02-28' },
  { id: 'sample-2', type: 'buyer', status: 'under-contract', propertyAddress: '456 Example Blvd, Bonita Springs, FL 34135', price: 689000, firstName: 'James', lastName: 'Whitfield', clientName: 'James Whitfield', keyDate: 'Closes Nov 14', closingDate: '2026-11-14', expirationDate: '2026-12-31' },
  { id: 'sample-3', type: 'buyer', status: 'pending', propertyAddress: '789 Demo Dr, Fort Myers, FL 33913', price: 540000, firstName: 'Linda', lastName: 'Okafor', clientName: 'Linda Okafor', keyDate: 'Inspection Oct 20', closingDate: '2026-12-05', expirationDate: '2026-11-30' },
  { id: 'sample-4', type: 'seller-listing', status: 'closed', propertyAddress: '10 Test Ct, Naples, FL 34119', price: 910000, firstName: 'Robert', lastName: 'Chen', clientName: 'Robert Chen', keyDate: 'Closed Sep 30', closingDate: '2026-09-30', expirationDate: '' },
];

function AgentDashboard({ currentUser }) {
  const { user, profile, userRole } = useAuth();
  const [reloadKey, setReloadKey] = useState(0);
  const photo = profile?.photoUrl || (userRole === 'Admin' ? ADMIN_FALLBACK_PHOTO : '');
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState({
    activeListings: 0,
    activeBuyers: 0,
    closedThisMonth: 0,
    pendingTasks: 0,
  });
  const [tasks, setTasks] = useState([]);
  const [txTasks, setTxTasks] = useState([]);
  const [showAllTasks, setShowAllTasks] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [rawTx, setRawTx] = useState([]);
  const [commissionSettings, setCommissionSettings] = useState(null);
  const [peek, setPeek] = useState(null);
  const closePeek = React.useCallback(() => setPeek(null), []);

  useEffect(() => {
    // Agent's tasks are loaded in their own effect below.
    const agentTasks = tasks;

    let cancelled = false;
    const load = async () => {
      // ?sample=1 shows the built-in examples without touching the database.
      const showSamples = new URLSearchParams(window.location.search).get('sample') === '1';
      let agentTransactions;
      if (showSamples) {
        agentTransactions = SAMPLE_TRANSACTIONS;
      } else if (user?.uid) {
        try {
          const saved = await getTeamTransactions(user.uid, userRole);
          setRawTx(saved);
          agentTransactions = saved.map(toCard);
        } catch (err) {
          console.error('Could not load transactions:', err);
          agentTransactions = [];
        }
      } else {
        agentTransactions = [];
      }
      if (cancelled) return;
      setTransactions(agentTransactions);
      recalc(agentTransactions, agentTasks);
    };
    const recalc = (agentTransactions, agentTasks) => {
    // Calculate metrics
    const activeListing = agentTransactions.filter(t => t.type === 'seller-listing' && t.status === 'listing-active').length;
    const activeBuyer = agentTransactions.filter(t => t.type === 'buyer' && t.status === 'under-contract').length;
    const closedMonth = agentTransactions.filter(t => t.status === 'closed').length;
    const pendingCount = agentTasks.filter(t => t.status === 'pending').length;

    setMetrics({
      activeListings: activeListing,
      activeBuyers: activeBuyer,
      closedThisMonth: closedMonth,
      pendingTasks: pendingCount,
    });
    };
    load();
    return () => { cancelled = true; };
  }, [currentUser, user, reloadKey]);

  // Pending and under contract are the same thing: "Under Contract", black.
  // Active Transactions shows only open work: active listings and under contract (closed is hidden).
  const openTransactions = transactions.filter(t => ['listing-active', 'under-contract', 'pending'].includes(t.status));

  // Listing active: white. Closed: gold.
  const getStatusColor = (status) => {
    switch (status) {
      case 'listing-active': return '#ffffff';
      case 'pending': return '#000000';
      case 'under-contract': return '#000000';
      case 'closed': return '#D4AF37';
      default: return '#000000';
    }
  };
  const getStatusText = (status) => (status === 'listing-active' || status === 'closed') ? '#000000' : '#ffffff';
  const getStatusLabel = (status) => {
    switch (status) {
      case 'listing-active': return 'Active Listing';
      case 'pending':
      case 'under-contract': return 'Under Contract';
      case 'closed': return 'Closed';
      default: return status || 'New';
    }
  };

  // Load the agent's tasks (transaction tasks for now).
  useEffect(() => {
    let cancelled = false;
    const loadTasks = async () => {
      if (!user?.uid) { setTasks([]); return; }
      try {
        const saved = await getTasks(user.uid);
        // Limited Agents have no lead/personal tasks on the dashboard.
        if (!cancelled) setTasks(showsLeadTasks(userRole) ? saved.filter(t => isMyTask(t, userRole)) : []);
      } catch (err) {
        console.error('Could not load tasks:', err);
        if (!cancelled) setTasks([]);
      }
    };
    loadTasks();
    return () => { cancelled = true; };
  }, [user?.uid, userRole, reloadKey]);

  // Load transaction tasks for the agent's own transactions (shared by role on each deal).
  useEffect(() => {
    let cancelled = false;
    loadTeamTransactionTasks(user?.uid, userRole).then(rows => { if (!cancelled) setTxTasks(rows); });
    return () => { cancelled = true; };
  }, [user?.uid, userRole, reloadKey]);

  const todayIso = todayET();
  const txLabelById = {};
  transactions.forEach(t => {
    txLabelById[t.id] = t.propertyAddress || t.address || t.name || t.title;
  });
  // Automatic emails stay off the dashboard task list in both views; they show only in a transaction's All view.
  const visibleTxTasks = txTasks.filter(t => !isAutomatic(t) && !hiddenFromLimited(t, userRole) && (showAllTasks || isRoleMine(t, userRole)));
  const dueToday = [...tasks, ...visibleTxTasks]
    .filter(t => t.status !== 'completed' && t.status !== 'na' && t.dueDate && t.dueDate <= todayIso)
    .map(t => ({ ...t, overdue: t.dueDate < todayIso }))
    .sort((a, b) => (a.dueDate < b.dueDate ? -1 : 1));
  const groupMap = {};
  // Only deal tasks are grouped here (no empty "Transaction" group for personal or lead tasks).
  dueToday.filter(t => t.transactionId).forEach(t => {
    const key = t.transactionId;
    if (!groupMap[key]) {
      groupMap[key] = { key, label: txLabelById[t.transactionId] || 'Transaction', items: [] };
    }
    groupMap[key].items.push(t);
  });
  const todayGroups = Object.values(groupMap);

  const markTaskDone = async (taskId) => {
    if (!user?.uid) return;
    try {
      await updateTask(user.uid, taskId, { status: 'completed' });
      setReloadKey(k => k + 1);
    } catch (err) {
      console.error('Could not mark task done:', err);
    }
  };

  // My Performance: this week's completed tasks, and commission after the team and broker splits.
  useEffect(() => {
    if (!user?.uid) return;
    get(ref(database, `users/${user.uid}/commissionSettings`))
      .then((snap) => setCommissionSettings(snap.val() || {}))
      .catch(() => setCommissionSettings({}));
  }, [user?.uid]);
  const weekStart = Date.now() - 7 * 24 * 60 * 60 * 1000;
  const tasksDoneThisWeek = txTasks.filter(t => t.status === 'completed' && t.completedAt && new Date(t.completedAt).getTime() >= weekStart && (!t.completedByUid || t.completedByUid === user?.uid)).length;
  // Take-home commission from config/commissionPlans (team leader cap plan or agent lead-source split + eXp cap).
  const myDeals = rawTx.filter(t => !t.ownerUid || t.ownerUid === user?.uid);
  const { ytd: ytdComm } = takeHome(myDeals, isTeamLeaderEmail(user?.email), new Date(), undefined, commissionSettings || {});
  const pendingCommission = pendingCommissionBreakdown(myDeals, isTeamLeaderEmail(user?.email), new Date(), undefined, commissionSettings || {}).total;
  const ytdCommission = ytdComm.net;
  const dollars = (n) => `$${Math.round(n).toLocaleString('en-US')}`;
  const performanceTiles = [
    { label: 'Tasks completed', value: String(tasksDoneThisWeek), sub: 'Last 7 days' },
    { label: 'Pending commission', value: dollars(pendingCommission), sub: 'Open deals · tap to see deals', to: '/pending-commission' },
    { label: 'Year to date commission', value: dollars(ytdCommission), sub: 'Closed this year' },
  ];

  return (
    <div style={{ padding: '2rem' }}>
      <div style={{
        marginBottom: '2rem',
        borderBottom: '1px solid #d8c9a8',
        paddingBottom: '1rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '18px' }}>
          {photo && (
            <img
              src={photo}
              alt={currentUser.firstName}
              style={{ width: 72, height: 72, borderRadius: '50%', objectFit: 'cover', border: '2px solid #D4AF37', flexShrink: 0 }}
            />
          )}
          <div>
          <h1 style={{ margin: '0 0 0.5rem 0', color: '#1a1a1a', fontFamily: "'Hurricane', cursive", fontWeight: 400, fontSize: '2.6rem', lineHeight: 1.15 }}>
            Welcome, {currentUser.firstName}!
          </h1>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
        <button
          onClick={() => navigate('/transactions/new')}
          style={{
            ...goldButton,
            padding: '10px 16px',
            borderRadius: '6px',
            fontWeight: '600',
            cursor: 'pointer',
            fontSize: '14px',
          }}>
          + New Transaction
        </button>
        </div>
      </div>

      {showsAnnouncements(userRole) && <AnnouncementFeed orgKey="residence" />}

      <PersonPeekPanel person={peek} onClose={closePeek} />
      <NicheNewsSection uid={user?.uid} />

      {/* Today's Tasks: transaction tasks now, lead tasks from the CRM later */}
      <section style={{ marginBottom: '2rem' }}>
        <h2 style={{ margin: '0 0 0.25rem 0', color: '#1a1a1a', fontSize: '1.25rem' }}>Today&apos;s Tasks</h2>
        <p style={{ margin: '0 0 1rem 0', color: '#7f7f7f', fontSize: '0.85rem' }}>
          Transaction tasks due today or overdue, grouped by transaction, then your open lead and pipeline tasks.
        </p>
        <p style={{ margin: '0 0 0.75rem 0' }}>
          <button type="button" onClick={() => setShowAllTasks(v => !v)} style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #b8a57a', background: '#fff', cursor: 'pointer', fontSize: 13 }}>
            {showAllTasks ? 'Showing all tasks on my deals' : 'Showing my tasks'} (switch)
          </button>
        </p>
        <EmailApprovalsPanel uid={user?.uid} isAdmin={userRole === 'Admin'} />
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: '1.25rem',
          alignItems: 'start',
        }}>
          <div style={{ backgroundColor: '#ffffff', borderRadius: '8px', padding: '1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #d8c9a8' }}>
            <div style={{ fontSize: '0.8rem', fontWeight: '700', letterSpacing: '0.08em', textTransform: 'uppercase', color: '#8C6D1F', marginBottom: '0.75rem' }}>Transaction Tasks</div>
            {todayGroups.length === 0 && (
              <div style={{ color: '#7f7f7f', textAlign: 'center', padding: '1.5rem' }}>Nothing due today. Nice work.</div>
            )}
            {todayGroups.map(group => (
              <div key={group.key} style={{ marginBottom: '1rem' }}>
                <div style={{ fontWeight: '700', color: '#1a1a1a', borderBottom: '1px solid #E1E4EA', paddingBottom: '0.35rem', marginBottom: '0.5rem' }}>
                  {group.label}
                  {(() => { const tx = rawTx.find(t => t.id === group.key); const who = tx && ([tx.firstName, tx.lastName].filter(Boolean).join(' ') || tx.clientName); return who ? (<span style={{ fontWeight: 400, fontSize: '0.9rem' }}> · <PersonName onOpen={() => setPeek({ transaction: tx, name: who })}>{who}</PersonName></span>) : null; })()}
                </div>
                {(() => {
                  const tx = rawTx.find(t => t.id === group.key) || {};
                  return (
                    <TransactionTasksList
                      compact
                      initialView="all"
                      onlyTaskIds={group.items.map(i => i.id)}
                      transactionId={group.key}
                      deal={tx}
                      ownerUid={tx.ownerUid || user?.uid}
                      dealAddress={tx.propertyAddress || tx.address || ''}
                      client={{ firstName: tx.firstName, lastName: tx.lastName, email: tx.email, phone: tx.phone }}
                      listingAgent={{ firstName: tx.homeSaleAgentFirstName, lastName: tx.homeSaleAgentLastName, email: tx.homeSaleAgentEmail, phone: tx.homeSaleAgentPhone }}
                    />
                  );
                })()}
              </div>
            ))}
          </div>
          {showsLeadTasks(userRole) && (
          <div style={{ backgroundColor: '#ffffff', borderRadius: '8px', padding: '1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #d8c9a8' }}>
            <div style={{ fontSize: '0.8rem', fontWeight: '700', letterSpacing: '0.08em', textTransform: 'uppercase', color: '#8C6D1F', marginBottom: '0.75rem' }}>Lead Tasks</div>
            <LeadTasksPanel tasks={tasks} uid={user?.uid} isAdmin={userRole === 'Admin'} senderName={currentUser?.firstName} onDone={markTaskDone} onRefresh={() => setReloadKey(k => k + 1)} />
          </div>
          )}
        </div>
      </section>


      <OutreachCard uid={user?.uid} />

      {/* My Performance */}
      <section style={{ marginBottom: '2rem' }}>
        <h2 style={{ margin: '0 0 1rem 0', color: '#1a1a1a', fontSize: '1.25rem' }}>My Performance</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
          {performanceTiles.map((t) => (
            <div key={t.label}
              {...(t.to ? { role: 'link', tabIndex: 0, 'aria-label': `${t.label} ${t.value}, view deals`, onClick: () => navigate(t.to), onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(t.to); } } } : {})}
              style={{ cursor: t.to ? 'pointer' : 'default', backgroundColor: '#ffffff', border: '1px solid #d8c9a8', borderLeft: '4px solid #D4AF37', borderRadius: '8px', padding: '1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
              <div style={{ fontSize: '0.8rem', fontWeight: '700', letterSpacing: '0.08em', textTransform: 'uppercase', color: '#8C6D1F' }}>{t.label}</div>
              <div style={{ fontSize: '1.6rem', fontWeight: '700', color: '#1a1a1a', margin: '0.5rem 0 0.25rem' }}>{t.value}</div>
              <div style={{ fontSize: '0.85rem', color: '#7f7f7f' }}>{t.sub}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Active Transactions (full width) */}
      <div style={{
        backgroundColor: '#ffffff',
        borderRadius: '8px',
        padding: '1.5rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        marginBottom: '2rem',
      }}>
        <h2 style={{ margin: '0 0 1.25rem 0', color: '#1a1a1a', borderBottom: '1px solid #E1E4EA', paddingBottom: '1rem' }}>
          Active Transactions
        </h2>

        {openTransactions.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {openTransactions.map(t => (
              <div
                key={t.id}
                onClick={() => navigate(String(t.id).startsWith('sample-') ? '/transactions' : `/transactions/${t.id}`)}
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr) auto',
                  gap: '1rem',
                  alignItems: 'center',
                  padding: '1rem 1.25rem',
                  backgroundColor: '#ffffff',
                  border: '1px solid #E1E4EA',
                  borderLeft: `4px solid ${t.status === 'listing-active' ? '#D4AF37' : getStatusColor(t.status)}`,
                  borderRadius: '6px',
                  cursor: 'pointer',
                }}
              >
                <div>
                  <div style={{ fontWeight: '600', color: '#1a1a1a', marginBottom: '4px' }}>{t.propertyAddress}</div>
                  <div style={{ fontSize: '0.85rem', color: '#7f7f7f' }}>
                    {t.clientName ? `${t.clientName} Â· ` : ''}{t.type === 'buyer' ? 'Buyer' : 'Seller'}
                  </div>
                </div>
                <div style={{ fontSize: '0.9rem', color: '#1a1a1a' }}>
                  <div style={{ color: '#7f7f7f', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Price</div>
                  ${t.price ? t.price.toLocaleString() : 'TBD'}
                </div>
                <div style={{ fontSize: '0.9rem', color: '#1a1a1a' }}>
                  <div style={{ color: '#7f7f7f', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Key date</div>
                  {t.keyDate || 'TBD'}
                </div>
                <div style={{
                  fontSize: '0.8rem',
                  color: getStatusText(t.status),
                  backgroundColor: getStatusColor(t.status),
                  border: '1px solid #000000',
                  padding: '0.3rem 0.6rem',
                  borderRadius: '3px',
                  whiteSpace: 'nowrap',
                }}>
                  {getStatusLabel(t.status)}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ color: '#7f7f7f', textAlign: 'center', padding: '2rem' }}>
            No active transactions
          </div>
        )}
      </div>

    </div>
  );
}

export default AgentDashboard;